import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { FullProjectIssue } from 'entities';

import { Column, Button } from 'components/Blocks';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { UserAvatarGroup } from 'components/UserAvatar';
import { FolderTreeIcon, FileSearchIcon, KeyboardIcon, UsersIcon } from 'components/Icons';

import { IssueComments } from './Comments';
import { ActionsTaken } from './ActionsTaken';

import { BaseProps } from './types';

const renderers: Array<{
  getLabel: (t: TFunction) => string;
  property: keyof FullProjectIssue;
  extractor?: (issue: FullProjectIssue & { t: TFunction }) => ReactNode;
  icon: ReactNode;
}> = [
  {
    getLabel: (t) => t('type'),
    property: 'type',
    extractor: ({ type, t }) => t(type),

    icon: <FileSearchIcon />,
  },
  {
    getLabel: (t) => t('sub-type'),
    property: 'subType',
    extractor: ({ subType, other, t }) => `${t(subType)}${other ? ` (${other})` : ''}`,
    icon: <FolderTreeIcon />,
  },
  {
    getLabel: (t) => t('description'),
    property: 'description',
    icon: <KeyboardIcon />,

    extractor: ({ description }) =>
      description
        .split('\n')
        .filter(Boolean)
        .map((text) => (
          <span key={text}>
            {text}
            <br />
          </span>
        )),
  },
];

export function MainInfo({ issue, updateIssue, setTab }: BaseProps) {
  const { t } = useLang();

  return (
    <Column>
      <PropertyWithIcon
        gap="3"
        icon={<UsersIcon />}
        name={t('users-involved')}
        alignItems="center"
        value={
          <Button variant="ghost" onClick={() => setTab('users')} className="mt-1">
            <UserAvatarGroup
              size={28}
              avatarClassName="text-sm"
              users={[...issue.salesInvolved, ...issue.executorsInvolved].map((data) => data.user)}
            />
          </Button>
        }
      />

      {renderers.map(({ getLabel, icon, property, extractor }) => (
        <PropertyWithIcon
          key={property}
          gap="3"
          icon={icon}
          name={getLabel(t)}
          value={extractor?.({ ...issue, t }) ?? (issue[property] as string)}
        />
      ))}

      <ActionsTaken issue={issue} updateIssue={updateIssue} />

      <IssueComments issue={issue} updateIssue={updateIssue} />
    </Column>
  );
}
